<template>
    <div id="edit-review" style="position: relative; top: 60px; margin-left: 13px;">
        <div class="row">
            <div class="bs-callout bs-callout-info">
                <h4>Review Editor</h4>
                <ul>
                    <li>Remove author names from the map name field</li>
                    <li>Double check pre-filled information with what's in the readme file</li>
                    <li>Not required, but site is intended to show screenshots in a 4:3 aspect ratio. (800x600, 1024x768, 1280x960)</li>
                    <li>Changes to screenshots happen immediately</li>
                    <li>Changes to map details happen after you click save</li>
                    <li>Checking Publish will add the review on the home page and show it under "Has Review" in browse</li>
                    <li>After saving click "View Review" to see your changes in a new window/tab</li>
                </ul>
            </div>
        </div>
        <div id="review-form" class="row">
            <div class="col-md-4">
                <div class="row" v-for="screenshot in screenshots" :key="screenshot.id">
                    <screenshot-editor :screen="screenshot" :reviewId="id" />
                </div>
            </div>
            <div class="col-md-6">
                <div class="row">
                    <div class="col-md-3">
                        <text-field label="Name" v-model="name" />
                    </div>
                   <div class="col-md-3">
                        <text-field label="Author" v-model="author" />
                    </div>
                   <div class="col-md-3">
                        <text-field label="Release Date" v-model="releaseDate" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3">
                        <text-field label="Filesize" v-model="filesize" />
                    </div>
                    <div class="col-md-3">
                        <dropdown label="Score" :options="scoreOptions" v-model="score"/>
                    </div>
                    <div class="col-md-3">
                        <dropdown label="Mods" :options="modOptions" v-model="mods" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3">
                        <dropdown label="Status" v-model="status" :options="statuses" />
                    </div>
                    <div class="col-md-3">
                        <text-field label="Reviewer" v-model="reviewer" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-9">
                        <check-box-group label="Gametypes" :options="gameTypeOptions" v-model="gameTypes" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-9">
                        <div class="form-group">
                            <label>Review</label>
                            <textarea class="form-control col-md-12" style="height: 300px; margin-bottom: 22px;" v-model="review"></textarea>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-9">
                        <text-field label="YouTube Link" v-model="youtube" />
                    </div>
                </div>
                <div class="row" v-if="youtubeEmbed">
                    <div class="col-md-9">
                        <iframe width="560" height="315" :src="youtubeEmbed" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-2">
                        <div class="form-group">
                            <a class="ol-featured_button" href="#" @click.prevent="save">Save</a>
                        </div>
                    </div>
                    <div class="col-md-2 col-md-offset-1">
                        <div class="form-group">
                            <router-link class="ol-featured_button" :to="`/review/${this.id}`" target="_blank">View Review</router-link>
                        </div>
                    </div>
                    <div class="col-md-2 col-md-offset-1" v-if="canDelete">
                        <div class="form-group">
                            <a class="ol-featured_button" href="#" @click.prevent="deleteReview">Delete</a>
                        </div>
                    </div>
                </div>
                <div class="row" v-if="readme">
                    <div class="col-md-12">
                        <h3>Readme File</h3>
                        <div class="bs-callout bs-callout-default left" v-html="readme"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { api } from '../framework/api';
import { copyCommonProperties } from '../framework/utils'
import { settings } from '../framework/settings';
import moment from 'moment'
import ScreenshotEditor from './ScreenshotEditor'
import Dropdown from './primitives/Dropdown'
import TextField from './primitives/TextField'
import CheckBoxGroup from './primitives/CheckBoxGroup'
import _ from 'lodash'
import toastr from 'toastr'

export default {
    components: { ScreenshotEditor, Dropdown, TextField, CheckBoxGroup },
    props: { id: String },
    data() {
        return {
            canDelete: false,
            name: null,
            author: null,
            releaseDate: null,
            filesize: null,
            score: null,
            mods: false,
            reviewer: null,
            readme: null,
            review: null,
            youtube: null,
            status: null,
            links: [],
            screenshots: [],
            gameTypes: [],
            gameTypeOptions: ['Death Match', 'Teams', 'CTF', 'KFC', 'Tag', 'Historical Mission', 'Single Player'],
            statuses : {
                'Pending': 'Pending',
                'Submitted': 'Submitted'
            },
            modOptions: {
                'No' : false,
                'Yes' : true
            },
            scoreOptions: {
                '10. Perfect': 10,
                '9. Superb': 9,
                '8. Great': 8,
                '7. Good': 7,
                '6. Borderline' : 6,
                '5. Bad' : 5,
                '4. I guess he was learning': 4,
                '3. This is no Moly': 3,
                '2. Horrible': 2,
                '1. Unplayable' : 1
            }
        }
    },
    watch: {
        youtube(newValue, oldValue) {
            if(this.links && this.links.length) {
                this.links[0].youtube = newValue;
            }
            else {
                this.links = [
                    { youtube: this.youtube }
                ]
            }
        }
    },
    computed: {
        youtubeEmbed() {
            if(!this.youtube) return null;
            let id = getYoutubeId(this.youtube);

            if(id) {
                return `//www.youtube.com/embed/${id}?rel=0`;
            }
            
            return null;

            function getYoutubeId(url) {
                var regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                var match = url.match(regExp);
                if(match && match[2].length == 11) {
                    return match[2];
                }

                return null;
            }
        }
    },
    methods: {
        async deleteReview() {
            if(confirm('Are you sure?')) {
                await api.delete(`reviews/${this.id}`);
                toastr.success('Review deleted');
            }
        },
        async save() {
            let ignore = ['scoreOptions', 'modOptions', 'statuses', 'gameTypeOptions', 'youtube', 'readme', 'sharedState', 'screenshots'];
            let data = { ...this.$data };
            ignore.forEach(prop => {
                delete data[prop];
            });
            if(this.status != 'Submitted') {
                data.reviewer = null;
            }
            await api.post(`reviews/${this.id}`, data);
            toastr.success('Review has been updated');
        }
    },
    async created() {
        let calls = [
            api.get(`reviews/${this.id}`),
            api.get(`reviews/${this.id}/readme`)
        ];
        let [review, readme] = await Promise.all(calls);
        this.readme = readme.data;
        Object.keys(this.$data).forEach((key) => {
            if(review.data[key]) {
                this.$data[key] = review.data[key];
            }
        });
        if(this.releaseDate) {
            this.releaseDate = moment(this.releaseDate).format('MM/DD/YYYY');
        }
        this.screenshots = review.data.screenshots.map(m => {
            return { id: _.uniqueId('screenshot_'), filename: m, src: `${settings.siteUrl}/screens/${m}` };
        });
        let numScreens = this.screenshots.length;
        while(numScreens < 6) {
            this.screenshots.push({ id: _.uniqueId('screenshot_'), filename: null, src:`${settings.siteUrl}/no_image_available_1_14.gif` });
            numScreens++;
        }
        if(review.data.links && review.data.links.length) {
            this.youtube = review.data.links[0].youtube;
        }
        if(!this.reviewer) {
            this.reviewer = this.sharedState.username;
        }
        this.canDelete = this.sharedState.isAdmin;
    },
}
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/EditReview.vue